// The room switcher: a row at the top of the conversation panel, one entry for each
// conversation the reader has, labelled the way the heading above the thread labels it.
//
// "Principals" never renders as user-facing text here (`docs/archived/09-web-client.md` and
// `docs/archived/13-visual-refresh.md`'s own acceptance criteria): the conversation shared
// with the others who have the same say is labelled with their first names, read from that
// thread — see `format.ts`'s `principalsRoomLabel`.

import { NavLink } from 'react-router-dom';

import type { Thread } from '../api/contract.js';

function UnreadDot({ thread }: { thread: Thread | null }): JSX.Element | null {
  if (thread?.unread !== true) {
    return null;
  }
  return (
    <span className="tabs__badge roomSwitcher__dot" aria-hidden="true">
      •
    </span>
  );
}

type RoomLinkProps = {
  to: string;
  active: boolean;
  label: string;
  thread: Thread | null;
  end?: boolean;
  /** Marks the reader's own 1:1 as private. */
  privateMarker?: boolean;
};

function RoomLink({
  to,
  active,
  label,
  thread,
  end = false,
  privateMarker = false,
}: RoomLinkProps): JSX.Element {
  return (
    <li className="roomSwitcher__item">
      <NavLink
        className={active ? 'roomSwitcher__button active' : 'roomSwitcher__button'}
        to={to}
        end={end}
      >
        {label}
        {privateMarker ? <span className="roomSwitcher__private">Private</span> : null}
        <UnreadDot thread={thread} />
      </NavLink>
    </li>
  );
}

export type RoomSwitcherProps = {
  base: string;
  landing: 'private' | 'principals' | 'conversation';
  ownThread: Thread | null;
  principalsThread: Thread | null;
  groupThread: Thread | null;
  /** "Maya" when this client can verify who is in the room, a phrase naming nobody
   * otherwise — see the header comment above. */
  principalsRoomLabel: string;
};

export function RoomSwitcher({
  base,
  landing,
  ownThread,
  principalsThread,
  groupThread,
  principalsRoomLabel,
}: RoomSwitcherProps): JSX.Element | null {
  // With fewer than two conversations there is nothing to switch between.
  const count = [ownThread, principalsThread, groupThread].filter(
    (thread) => thread !== null,
  ).length;
  if (count < 2) {
    return null;
  }
  return (
    <nav className="roomSwitcher" aria-label="Conversations">
      <ul className="roomSwitcher__list">
        {ownThread === null ? null : (
          <RoomLink
            to={`${base}/private`}
            active={landing === 'private'}
            label="You and Mor"
            thread={ownThread}
            privateMarker
          />
        )}
        {principalsThread === null ? null : (
          <RoomLink
            to={`${base}/principals`}
            active={landing === 'principals'}
            label={`You, ${principalsRoomLabel} and Mor`}
            thread={principalsThread}
          />
        )}
        {groupThread === null ? null : (
          <RoomLink
            to={base}
            end
            active={landing === 'conversation'}
            label="Everyone and Mor"
            thread={groupThread}
          />
        )}
      </ul>
    </nav>
  );
}
